import React, { useEffect, useState } from 'react';
import {
  Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, Field, Input,
  MessageBar, MessageBarBody, OverlayDrawer, Select, Spinner, Switch, Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import type { Connection } from '../../api/types';
import { initialSyncSchedule, scheduleToMinutes, validateSchedule, type ScheduleUnit } from './syncSchedule';

interface Props {
  connection: Connection;
  open: boolean;
  onSaved: (connection: Connection) => void;
  onClose: () => void;
}

const ConnectionScheduleEditor: React.FC<Props> = ({ connection, open, onSaved, onClose }) => {
  const policy = connection.connector.capabilities?.scheduled_sync;
  const [schedule, setSchedule] = useState(() => initialSyncSchedule(policy, connection.syncIntervalMinutes));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const validationError = validateSchedule(schedule, policy);

  useEffect(() => {
    if (open) {
      setSchedule(initialSyncSchedule(policy, connection.syncIntervalMinutes));
      setError('');
    }
  }, [connection.syncIntervalMinutes, open, policy]);

  const save = async () => {
    if (loading || validationError) return;
    setLoading(true);
    setError('');
    try {
      const saved = await connectionsService.updateConnection(connection.id, {
        syncIntervalMinutes: schedule.enabled ? scheduleToMinutes(schedule) : null,
      });
      onSaved(saved);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Failed to update sync schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <OverlayDrawer className="sync-schedule-drawer" position="end" size="small" open={open}
      onOpenChange={(_, data) => { if (!data.open && !loading) onClose(); }}>
      <DrawerHeader>
        <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Close sync schedule"
          icon={<Dismiss24Regular />} disabled={loading} onClick={onClose} />}>
          Sync schedule
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className="sync-schedule-drawer-body">
        <Text>Choose how often data is synchronized for {connection.name}.</Text>
        <Switch label="Scheduled synchronization" checked={schedule.enabled} disabled={loading}
          onChange={(_, data) => setSchedule(current => ({ ...current, enabled: data.checked }))} />
        {schedule.enabled &&
          <Field label="Repeat every" validationState={validationError ? 'error' : 'none'}
            validationMessage={validationError || undefined}
            hint={!validationError && policy ? `Allowed range: ${policy.minimum_minutes}–${policy.maximum_minutes} minutes.` : undefined}>
            <div className="sync-schedule-interval">
              <Input className="sync-schedule-amount" type="number" min={1} step={1} value={String(schedule.amount)} disabled={loading}
                onChange={(_, data) => setSchedule(current => ({ ...current, amount: Number(data.value) }))} />
              <Select aria-label="Unit" value={schedule.unit} disabled={loading}
                onChange={(_, data) => setSchedule(current => ({ ...current, unit: data.value as ScheduleUnit }))}>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </Select>
            </div>
          </Field>
        }
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      </DrawerBody>
      <DrawerFooter>
        <Button appearance="primary" disabled={loading || !!validationError} onClick={save}
          icon={loading ? <Spinner size="tiny" /> : undefined}>{loading ? 'Saving…' : 'Save schedule'}</Button>
        <Button disabled={loading} onClick={onClose}>Cancel</Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default ConnectionScheduleEditor;